/**
 * LoginScreen — email/password sign-in (Supabase Auth).
 *
 * UX standards (CLAUDE.md §2.2): loading state on submit, button disabled while
 * processing, friendly + actionable errors — never a raw Supabase error blob.
 */
import { useState, type FormEvent } from "react";
import { useAuth } from "@/auth/AuthProvider";

/** Map Supabase auth errors to plain-language messages. */
function friendlyAuthError(err: unknown): string {
  const message = err instanceof Error ? err.message.toLowerCase() : "";
  if (message.includes("invalid login credentials")) {
    return "That email and password don't match. Please try again.";
  }
  if (message.includes("email not confirmed")) {
    return "Your account isn't confirmed yet. Check your email for a confirmation link.";
  }
  if (message.includes("network") || message.includes("fetch")) {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  return "Sign-in failed. Please try again, or contact your manager if it keeps happening.";
}

export function LoginScreen() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      // On success, AuthProvider's onAuthStateChange updates context and the
      // router swaps to the authenticated app — nothing else to do here.
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-sm">
        <h1 className="text-lg font-semibold">TK Call Coach</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Sign in to review and coach your calls.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div className="space-y-1">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="text-sm font-medium">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !email || !password}
            className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
